/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { styleguideColors as COLORS, styleguideFontSize as FONT_SIZE, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { styleguideFontFamily as FONT, styleguideLetterSpacing as TRACKING } from '@kaiizen/kai-shiki/core';

/**
 * The Org Settings surface vocabulary, in the 2026 design language (KAIDEV-4345).
 *
 * These names used to alias `SETTINGS_*` in `Client/Styles`, the interim settings vocabulary that
 * predates the redesign. They are defined here instead because that module is shared with three
 * Super Admin surfaces and the main navigation, which have not moved yet — changing it in place
 * would have restyled them silently. It stays as it is until those move, and then goes.
 *
 * Everything below comes from the kai-shiki seam, which is the palette and type scale the design
 * is drawn in.
 */
export const ORG_SETTINGS_COLORS = {
  pageBackground: COLORS.paper,
  white: COLORS.white,
  text: COLORS.ink,
  textMuted: COLORS.midtone,
  textMutedLight: COLORS.textTertiary,
  textSoft: COLORS.midtoneFaded,
  textFieldLabel: COLORS.midtone,
  border: COLORS.stroke,
  /** The filled bar a locked or captioned block sits under. */
  panelHeaderBackground: COLORS.shell,
  /** The insight banner is the one blue surface the design keeps. */
  infoBlue: COLORS.info,
  infoBlueBackground: COLORS.infoFill,
  green: COLORS.success,
  red: COLORS.error,
  disabledButtonBorder: COLORS.strokeFaded,
} as const;

export const ORG_SETTINGS_SPACING = {
  gutterX: 26,
  standardPadding: 26,
  sectionInsetTop: 26,
} as const;

export const ORG_SETTINGS_SECTION_INSET_STYLE = css`
  label: ORG_SETTINGS_SECTION_INSET_STYLE;
  width: 100%;
  padding: ${ORG_SETTINGS_SPACING.sectionInsetTop}px ${ORG_SETTINGS_SPACING.gutterX}px 0;
`;

/* Sharp-cornered, as the 2026 language is throughout: the only radii it keeps are the circle and
   the toggle's own pill. */
export const ORG_SETTINGS_PANEL_STYLE = css`
  label: ORG_SETTINGS_PANEL_STYLE;
  width: 100%;
  border: 1px solid ${ORG_SETTINGS_COLORS.border};
  background: ${ORG_SETTINGS_COLORS.white};
  overflow: hidden;
`;

export const ORG_SETTINGS_PANEL_HEADER_STYLE = css`
  label: ORG_SETTINGS_PANEL_HEADER_STYLE;
  padding: 13px 18px;
  display: flex;
  align-items: center;
  background: ${ORG_SETTINGS_COLORS.panelHeaderBackground};
  border-bottom: 1px solid ${ORG_SETTINGS_COLORS.border};
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
  font-weight: ${FONT_WEIGHT.bold};
  line-height: 1.25;
  color: ${ORG_SETTINGS_COLORS.text};
`;

export const ORG_SETTINGS_SUBHEADING_STYLE = css`
  label: ORG_SETTINGS_SUBHEADING_STYLE;
  font-family: ${FONT.display};
  font-size: ${pxToRem(FONT_SIZE.displaySmall)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: -0.01em;
  line-height: 1.333;
  color: ${ORG_SETTINGS_COLORS.text};
`;

export const ORG_SETTINGS_BODY_TEXT_STYLE = css`
  label: ORG_SETTINGS_BODY_TEXT_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.45;
  color: ${ORG_SETTINGS_COLORS.textMuted};
`;

export const ORG_SETTINGS_BODY_TEXT_LIGHT_STYLE = css`
  label: ORG_SETTINGS_BODY_TEXT_LIGHT_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodySmall)};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.45;
  color: ${ORG_SETTINGS_COLORS.textMutedLight};
`;

export const ORG_SETTINGS_CAPTION_STYLE = css`
  label: ORG_SETTINGS_CAPTION_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodyMicro)};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.6;
  color: ${ORG_SETTINGS_COLORS.textMuted};
`;

/** A page or section title: the display face, as every 2026 heading is. */
export const ORG_SETTINGS_TITLE_REGULAR_STYLE = css`
  label: ORG_SETTINGS_TITLE_REGULAR_STYLE;
  font-family: ${FONT.display};
  font-size: ${pxToRem(FONT_SIZE.displayModalTitle)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: -0.01em;
  line-height: 1.2;
  color: ${ORG_SETTINGS_COLORS.text};
`;

/* Buttons are mono, uppercase and square — the design's one button shape, at two sizes. */
const ACTION_BUTTON_BASE_STYLE = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  text-transform: uppercase;
  white-space: nowrap;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(FONT_SIZE.monoStageNum)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: ${TRACKING.monoTight};
  line-height: 1.167;
`;

export const ORG_SETTINGS_SMALL_ACTION_BUTTON_STYLE = css`
  ${ACTION_BUTTON_BASE_STYLE};
  label: ORG_SETTINGS_SMALL_ACTION_BUTTON_STYLE;
  min-height: 32px;
  padding: 8px 15px;
  background: transparent;
  color: ${ORG_SETTINGS_COLORS.text};
`;

export const ORG_SETTINGS_MEDIUM_ACTION_BUTTON_STYLE = css`
  ${ACTION_BUTTON_BASE_STYLE};
  label: ORG_SETTINGS_MEDIUM_ACTION_BUTTON_STYLE;
  min-height: 40px;
  padding: 11px 20px;
`;

/** The design's primary button: ink ground, paper text. */
export const ORG_SETTINGS_PRIMARY_ACTION_BUTTON_STYLE = css`
  ${ACTION_BUTTON_BASE_STYLE};
  label: ORG_SETTINGS_PRIMARY_ACTION_BUTTON_STYLE;
  min-height: 40px;
  padding: 11px 20px;
  border: 1px solid ${ORG_SETTINGS_COLORS.text};
  background: ${ORG_SETTINGS_COLORS.text};
  color: ${COLORS.paper};

  &:disabled {
    background: ${ORG_SETTINGS_COLORS.border};
    border-color: ${ORG_SETTINGS_COLORS.border};
    color: ${ORG_SETTINGS_COLORS.textMuted};
    cursor: not-allowed;
  }
`;

export const ORG_SETTINGS_PAGE_TITLE_STYLE = css`
  label: ORG_SETTINGS_PAGE_TITLE_STYLE;
  font-family: ${FONT.display};
  font-size: ${pxToRem(FONT_SIZE.displayLarge)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: -0.02em;
  color: ${ORG_SETTINGS_COLORS.text};
  white-space: nowrap;
`;

export const ORG_SETTINGS_PAGE_BODY_STYLE = css`
  label: ORG_SETTINGS_PAGE_BODY_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(FONT_SIZE.bodyMedium)};
  font-weight: ${FONT_WEIGHT.regular};
  line-height: 1.45;
  color: ${ORG_SETTINGS_COLORS.textMuted};
`;
